/**
 * WHAT:
 *   Scan results component displaying scan progress, navigation, and PDF information.
 *
 * WHY:
 *   Centralizes scan results UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - formatNumber2 utility function
 *   - validateParsedData utility function
 *   - TypeScript types (ScanResultsProperties, ScanProgressState)
 *
 * RISKS:
 *   - NOTE: Component renders large amounts of data - may impact performance
 *   - NOTE: Complex conditional rendering logic - ensure all edge cases handled
 *   - WARN: PDF navigation may fail if index is out of bounds
 */

import type { ScanResultsProperties } from "../types";
import type { FC } from "react";

/**
 * WHAT:
 *   Component displaying scan results with progress, navigation, and PDF details.
 *
 * WHY:
 *   Encapsulates scan results display UI logic.
 *
 * @param root0
 *
 * @param root0.scanResults
 *
 * @param root0.parsedPdfs
 *
 * @param root0.failedPdfs
 *
 * @param root0.currentPdfIndex
 *
 * @param root0.browseFailedOnly
 *
 * @param root0.scanProgress
 *
 * @param root0.securitiesDataMap
 *
 * @param root0.onPreviousPdf
 *
 * @param root0.onNextPdf
 *
 * @param root0.onToggleBrowseMode
 *
 * @param root0.formatNumber2
 *
 * @returns {JSX.Element | null} Scan results component or null if scanResults is null
 *
 */
// Reason: Component is exported via barrel export in index.ts, not directly imported elsewhere

export const ScanResults: FC<ScanResultsProperties> = ({
  browseFailedOnly,
  currentPdfIndex,
  failedPdfs,
  formatNumber2: formatNumber2Property,
  onNextPdf,
  onPreviousPdf,
  onToggleBrowseMode,
  parsedPdfs,
  scanProgress,
  scanResults,
  securitiesDataMap,
}) => {
  if (!scanResults) {
    return null;
  }

  const { avgMsPerPdf, currentFile, elapsedMs, pdfsPerSec } = scanProgress;

  const currentList = browseFailedOnly ? failedPdfs : parsedPdfs;
  const currentLength = currentList.length;
  const currentPdf = currentList[currentPdfIndex];

  // Helper to get display data for current PDF
  const getDisplayData = () => {
    if (!currentPdf) {
      return null;
    }
    if (browseFailedOnly) {
      return currentPdf.parsed_data ?? currentPdf.data ?? currentPdf;
    }
    return currentPdf.data ?? currentPdf;
  };

  const displayData = getDisplayData();
  const isin =
    displayData &&
    typeof displayData === "object" &&
    "isin" in displayData &&
    typeof displayData.isin === "string"
      ? displayData.isin
      : null;
  const securitiesData = isin ? (securitiesDataMap[isin] ?? null) : null;

  // Helper to render progress stats
  const renderProgressStats = () => (
    <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-300">
      <div className="bg-slate-700/30 rounded p-2">
        <div className="text-slate-400">Elapsed</div>
        <div className="font-mono">{(elapsedMs / 1000).toFixed(1)} s</div>
      </div>
      <div className="bg-slate-700/30 rounded p-2">
        <div className="text-slate-400">PDFs/s</div>
        <div className="font-mono">{pdfsPerSec.toFixed(2)}</div>
      </div>
      <div className="bg-slate-700/30 rounded p-2">
        <div className="text-slate-400">Sekunden/PDF</div>
        <div className="font-mono">{(avgMsPerPdf / 1000).toFixed(2)} s</div>
      </div>
      <div className="bg-slate-700/30 rounded p-2">
        <div className="text-slate-400">Aktuelle Datei</div>
        <div className="font-mono truncate">{currentFile || "-"}</div>
      </div>
    </div>
  );

  // Helper to render early abort warning
  const renderEarlyAbortWarning = () => {
    if (scanResults.failed !== 1 || scanResults.successful !== 0) {
      return null;
    }
    return (
      <div className="mb-4 p-3 bg-orange-600/20 rounded-lg border border-orange-500/30">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-orange-400">⚠️</span>
          <span className="text-orange-400 font-medium">Scan abgebrochen</span>
        </div>
        <div className="text-orange-300 text-sm">
          Der Scan wurde nach der ersten fehlerhaften PDF abgebrochen. Raw Output wird unten
          angezeigt für detaillierte Analyse.
        </div>
      </div>
    );
  };

  // Helper to render navigation controls
  const renderNavigationControls = () => {
    if (currentLength === 0) {
      return null;
    }

    const navigationText = browseFailedOnly
      ? `⚠️ ${String(currentPdfIndex + 1)} / ${String(currentLength)} Fehlerhafte PDFs`
      : `✅ ${String(currentPdfIndex + 1)} / ${String(currentLength)} Erfolgreiche PDFs`;

    const navigationColor = browseFailedOnly ? "text-red-400" : "text-green-400";

    return (
      <div className="flex items-center justify-between mb-4 p-3 bg-slate-600/20 rounded-lg">
        <button
          className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded text-sm font-medium hover:bg-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition"
          disabled={currentPdfIndex === 0}
          onClick={onPreviousPdf}
        >
          ← Previous
        </button>

        <div className="flex items-center gap-3">
          <div className={`text-sm ${navigationColor}`}>{navigationText}</div>

          {/* Toggle zwischen erfolgreichen und fehlerhaften PDFs */}
          {failedPdfs.length > 0 && parsedPdfs.length > 0 && (
            <button
              className="px-2 py-1 text-xs bg-slate-700/50 text-slate-300 rounded hover:bg-slate-700/70 transition"
              onClick={() => {
                onToggleBrowseMode(!browseFailedOnly);
              }}
              title={browseFailedOnly ? "Zeige erfolgreiche PDFs" : "Zeige nur fehlerhafte PDFs"}
            >
              {browseFailedOnly ? "✅ Erfolgreiche" : "⚠️ Fehlerhafte"}
            </button>
          )}
        </div>

        <button
          className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded text-sm font-medium hover:bg-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition"
          disabled={currentPdfIndex === currentLength - 1}
          onClick={onNextPdf}
        >
          Next →
        </button>
      </div>
    );
  };

  // Helper to render debug info
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV !== "development") {
      return null;
    }
    return (
      <div className="mb-4 p-3 bg-yellow-600/20 rounded-lg">
        <div className="text-yellow-400 text-sm font-medium mb-2">🐛 Debug Info:</div>
        <div className="text-yellow-300 text-xs">
          <div>parsedPdfs.length: {parsedPdfs.length}</div>
          <div>currentPdfIndex: {currentPdfIndex}</div>
          <div>scanResults: {JSON.stringify(scanResults)}</div>
        </div>
      </div>
    );
  };

  // Helper to render error info
  const renderErrorInfo = () => {
    if (scanResults.failed === 0) {
      return null;
    }

    return (
      <div className="mb-4 p-3 bg-red-600/20 rounded-lg">
        <div className="text-red-400 text-sm mb-2">
          <strong>⚠️ {scanResults.failed} PDFs konnten nicht geparst werden</strong>
        </div>

        {/* Show first few errors */}
        {failedPdfs.slice(0, 3).map((failedPdf) => {
          const pdfKey = `${failedPdf.file_name ?? "unknown"}-${failedPdf.error ?? "no-error"}`;
          return (
            <div className="text-red-300 text-xs mt-1 p-2 bg-red-800/20 rounded" key={pdfKey}>
              <div className="font-medium">{failedPdf.file_name}</div>
              <div className="text-red-400 mt-1">{failedPdf.error}</div>
            </div>
          );
        })}

        {failedPdfs.length > 3 && (
          <div className="text-red-300 text-xs mt-2">
            ... und {failedPdfs.length - 3} weitere Fehler
          </div>
        )}
      </div>
    );
  };

  // Helper to render no results message
  const renderNoResultsMessage = () => {
    if (parsedPdfs.length > 0) {
      return null;
    }
    return (
      <div className="mb-4 p-4 bg-slate-600/20 rounded-lg">
        <div className="text-slate-400 text-center">
          <div className="text-lg mb-2">📄 Keine geparsten PDFs gefunden</div>
          <div className="text-sm">
            Alle PDFs konnten nicht geparst werden oder es wurden keine PDFs im Ordner gefunden.
          </div>
        </div>
      </div>
    );
  };

  // Helper to render current PDF info
  const renderCurrentPdfInfo = () => {
    if (!currentPdf) {
      return null;
    }

    const fileName = currentPdf.file_name ?? "Unbekannt";

    if (browseFailedOnly) {
      const error = currentPdf.error ?? "Unbekannter Fehler";
      return (
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-slate-400">📄 Datei:</span>
            <span className="text-white font-medium">{fileName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">❌ Fehler:</span>
            <span className="text-red-400">{error}</span>
          </div>
        </div>
      );
    }

    const broker = currentPdf.broker ?? "Unbekannt";
    const transactionType = currentPdf.transaction_type ?? "Unbekannt";

    return (
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-slate-400">📄 Datei:</span>
          <span className="text-white font-medium">{fileName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400">🏢 Broker:</span>
          <span className="text-blue-400">{broker}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400">📋 Typ:</span>
          <span className="text-green-400">{transactionType}</span>
        </div>
      </div>
    );
  };

  // Helper to render parsed data display
  const renderParsedDataDisplay = () => {
    if (!displayData) {
      return null;
    }
    return (
      <div>
        <h4 className="text-md font-semibold text-white mb-2">📋 Normalisiertes JSON</h4>
        <pre className="bg-slate-800 p-3 rounded-lg text-xs text-slate-300 overflow-auto max-h-64">
          {JSON.stringify(displayData, null, 2)}
        </pre>
      </div>
    );
  };

  // Helper to render mapping overview
  const renderMappingOverview = () => {
    if (!displayData || typeof displayData !== "object" || !("_schema" in displayData)) {
      return null;
    }

    interface DisplayDataWithSchema {
      [key: string]: unknown;
      _schema?: {
        mapped?: string[];
        unmapped?: string[];
      };
    }
    const typedDisplayData = displayData as DisplayDataWithSchema;
    const schema = typedDisplayData._schema;
    const mapped: string[] = Array.isArray(schema?.mapped) ? schema.mapped : [];
    const unmapped: string[] = Array.isArray(schema?.unmapped) ? schema.unmapped : [];

    return (
      <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
        <h4 className="text-md font-semibold text-white mb-3">🗺️ Mapping Overview</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-900/20 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-green-400 mb-2">✅ Successfully Mapped</h5>
            <div className="space-y-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
              {mapped.length > 0 ? (
                mapped.map((field) => {
                  const fieldValue = typedDisplayData[field];
                  const displayValue =
                    field === "dividend_per_share"
                      ? formatNumber2Property(fieldValue)
                      : String(fieldValue);
                  return (
                    <div className="text-xs" key={field}>
                      <span className="text-green-300 font-medium">{field}:</span>
                      <span className="text-green-100 ml-2">{displayValue}</span>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-red-300">No fields mapped</div>
              )}
            </div>
          </div>
          <div className="bg-red-900/20 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-red-400 mb-2">❌ Not Mapped</h5>
            <div className="space-y-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
              {unmapped.length > 0 ? (
                unmapped.map((field) => (
                  <div className="text-xs" key={field}>
                    <span className="text-red-300 font-medium">{field}:</span>
                    <span className="text-red-100 ml-2">Empty</span>
                  </div>
                ))
              ) : (
                <div className="text-xs text-green-300">All required fields mapped</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper to create transaction data without schema/validation
  const createTransactionData = () => {
    if (!displayData || typeof displayData !== "object") {
      return {};
    }
    interface DisplayDataWithSchema {
      [key: string]: unknown;
      _schema?: unknown;
      _validation?: unknown;
    }
    const typedDisplayData = displayData as DisplayDataWithSchema;
    const transactionData: Record<string, unknown> = {
      ...typedDisplayData,
    };
    delete transactionData._schema;
    delete transactionData._validation;
    return transactionData;
  };

  // Helper to create securities data object
  const createSecuritiesDataObject = () => {
    if (!securitiesData || typeof securitiesData !== "object") {
      return {
        _note: "Securities-Daten werden automatisch geladen, wenn ISIN vorhanden ist",
        dividend_schedule: null,
        price_cache: null,
        securities: null,
        securities_metrics: null,
      };
    }

    const dividendSchedule =
      "dividend_schedule" in securitiesData ? (securitiesData.dividend_schedule ?? null) : null;
    const priceCache =
      "price_cache" in securitiesData ? (securitiesData.price_cache ?? null) : null;
    const securities = "securities" in securitiesData ? (securitiesData.securities ?? null) : null;
    const securitiesMetrics =
      "securities_metrics" in securitiesData ? (securitiesData.securities_metrics ?? null) : null;

    return {
      dividend_schedule: dividendSchedule,
      price_cache: priceCache,
      securities: securities,
      securities_metrics: securitiesMetrics,
    };
  };

  // Helper to render complete result JSON
  const renderCompleteResultJson = () => {
    if (!displayData) {
      return null;
    }

    const transactionData = createTransactionData();
    const securitiesDataObj = createSecuritiesDataObject();

    return (
      <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
        <h4 className="text-md font-semibold text-white mb-3">📦 Complete Result JSON</h4>
        <div className="mb-2 text-xs text-slate-400">
          Alle geparsten Daten + vollständige Securities-Daten (wie für Supabase)
          {!securitiesData && isin ? (
            <span className="ml-2 text-yellow-400">
              (Lade Securities-Daten für ISIN: {isin}...)
            </span>
          ) : null}
        </div>
        <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-auto max-h-96 border border-slate-700">
          {JSON.stringify(
            {
              ...transactionData,
              ...securitiesDataObj,
            },
            null,
            2,
          )}
        </pre>
      </div>
    );
  };

  // Helper to calculate summary statistics
  const calculateSummaryStats = () => {
    if (!currentPdf || browseFailedOnly || typeof currentPdf !== "object") {
      return null;
    }

    interface PdfWithSchema {
      _schema?: {
        required?: string[];
        mapped?: string[];
        unmapped?: string[];
        completion_percentage?: number;
      };
    }

    if (!("_schema" in currentPdf)) {
      return null;
    }

    const typedCurrentPdf = currentPdf as PdfWithSchema;
    const schema = typedCurrentPdf._schema;
    const total = Array.isArray(schema?.required) ? schema.required.length : 0;
    const mapped = Array.isArray(schema?.mapped) ? schema.mapped.length : 0;
    const unmapped = Array.isArray(schema?.unmapped)
      ? schema.unmapped.length
      : Math.max(total - mapped, 0);

    let completion: number;
    if (typeof schema?.completion_percentage === "number") {
      completion = schema.completion_percentage;
    } else if (total > 0) {
      completion = Math.round((mapped / total) * 100);
    } else {
      completion = 0;
    }

    return { completion, mapped, total, unmapped };
  };

  // Helper to render summary
  const renderSummary = () => {
    const stats = calculateSummaryStats();
    if (!stats) {
      return null;
    }

    const { completion, mapped, total, unmapped } = stats;

    return (
      <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
        <h4 className="text-md font-semibold text-white mb-3">📈 Summary (Current PDF)</h4>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{mapped}</div>
            <div className="text-slate-400">Mapped</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400">{unmapped}</div>
            <div className="text-slate-400">Not Mapped</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{total}</div>
            <div className="text-slate-400">Total Fields</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{completion}%</div>
            <div className="text-slate-400">Completion</div>
          </div>
        </div>
      </div>
    );
  };

  // Helper to render debug navigation message
  const renderDebugNavigationMessage = () => {
    if (parsedPdfs.length > 0 || scanResults.successful === 0) {
      return null;
    }
    return (
      <div className="mb-4 p-3 bg-orange-600/20 rounded-lg">
        <div className="text-orange-400 text-sm font-medium mb-2">
          ⚠️ Debug: Navigation nicht sichtbar
        </div>
        <div className="text-orange-300 text-xs">
          <div>parsedPdfs.length: {parsedPdfs.length}</div>
          <div>scanResults.successful: {scanResults.successful}</div>
          <div>scanResults.total: {scanResults.total}</div>
          <div>Möglicherweise werden die geparsten PDFs nicht korrekt gesetzt.</div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6 p-4 bg-slate-600/20 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-white">📊 Scan-Ergebnisse</h3>
        <div className="text-sm text-slate-400">
          {scanResults.successful} von {scanResults.total} erfolgreich
        </div>
      </div>

      {renderProgressStats()}
      {renderEarlyAbortWarning()}
      {renderNavigationControls()}
      {renderDebugInfo()}
      {renderErrorInfo()}
      {renderNoResultsMessage()}
      {renderCurrentPdfInfo()}
      {renderParsedDataDisplay()}
      {renderMappingOverview()}
      {renderCompleteResultJson()}
      {renderSummary()}
      {renderDebugNavigationMessage()}
    </div>
  );
};
